import {
  ReservedPaidMembershipPlan,
  SubscribedPaidMembershipPlan,
} from '@/models/api/paid-member-price/member-plan/available-membership-plan'
import {
  AvailablePaidMembershipPlanGroup,
} from '@/models/api/paid-member-price/member-plan/group/available-paid-membership-plan-group'
import {
  DefaultAvailableMembershipPlanGroupRepository,
  ListAvailablePaidMembershipPlanGroupRepository,
} from '@/models/api/paid-member-price/member-plan/group/available-paid-membership-plan-group-repository'
import { PaidMemberSubscriptionService } from '@/services/api/paid-member-subscription'
import { PlanGroupViewModel } from '@spa/components/pages/mypage/paid-membership/plans/models/plan-group'
import { Either, left, right } from 'fp-ts/lib/Either'
export interface PlanGroupViewModelRepository {
  list(): Promise<Either<Error[], PlanGroupViewModel[]>>
}

export class DefaultPlanGroupViewModelRepository implements PlanGroupViewModelRepository {
  static get forHost(): PlanGroupViewModelRepository {
    return new DefaultPlanGroupViewModelRepository(
      DefaultAvailableMembershipPlanGroupRepository.forHost,
      new PaidMemberSubscriptionService()
    )
  }
  constructor(
    private readonly paidMembershipPlanGroupRepository: ListAvailablePaidMembershipPlanGroupRepository,
    private readonly paidMemberSubscriptionService: PaidMemberSubscriptionService
  ) {}

  async list(): Promise<Either<Error[], PlanGroupViewModel[]>> {
    const groups = await this.paidMembershipPlanGroupRepository.listAvailables()

    const results = await Promise.all(groups.map(g => this.joinNextPayment(g)))

    const errors = results.reduce(
      (prev: Error[], result) => result.fold(
        error => [...prev, error],
        _r => prev
      ),
      []
    )
    if (errors.length > 0) {
      return left(errors)
    }

    const planGroups = results.reduce(
      (prev: PlanGroupViewModel[], result) => result.fold(
        _l => prev,
        planGroup => [...prev, planGroup]
      ),
      []
    )

    return right(planGroups)
  }

  private async joinNextPayment(group: AvailablePaidMembershipPlanGroup): Promise<Either<Error, PlanGroupViewModel>> {
    /**
     * 同一グループで同時に複数のプランに加入することがない前提で、
     * 次回支払いの情報を取得する必要があるプランは多くても1件としている
     */
    const planNeedNextPayment = group.plans.find(p => p.subscribed() || p.reserved()) as PlanNeedNextPayment | undefined

    if (!planNeedNextPayment) {
      return right(PlanGroupViewModel.createWithoutNextPayment(group))
    }

    const maybeNextPayment = await this.paidMemberSubscriptionService
      .getNextPayment(planNeedNextPayment.externalId, planNeedNextPayment.subscribeId)

    return maybeNextPayment.fold(
      error => left(error),
      nextPayment => right(PlanGroupViewModel.createWithNextPayment(group, nextPayment))
    )
  }
}

type PlanNeedNextPayment = SubscribedPaidMembershipPlan | ReservedPaidMembershipPlan
